import { IconOption } from 'rwl-shared/dist/models/widget';
import { faHouse, faLocationDot } from '@fortawesome/pro-solid-svg-icons';
import { NeighborhoodServiceAreaInfo } from 'rwl-shared/dist/models';

/**
 * Filter out outlier bounds using IQR method based on distance from centroid.
 * This focuses the map on the main cluster of markers.
 */
function filterOutlierBounds(
    boundsArray: google.maps.LatLngBoundsLiteral[]
): google.maps.LatLngBoundsLiteral[] {
    if (boundsArray.length <= 3) {
        // Not enough points to determine outliers
        return boundsArray;
    }

    // Calculate centroid of all bounds
    const centers = boundsArray.map((b) => ({
        lat: (b.north + b.south) / 2,
        lng: (b.east + b.west) / 2,
    }));

    const centroid = {
        lat: centers.reduce((sum, c) => sum + c.lat, 0) / centers.length,
        lng: centers.reduce((sum, c) => sum + c.lng, 0) / centers.length,
    };

    // Calculate distances from centroid
    const distances = centers.map((c) => getDistanceMeters(c, centroid));

    // Sort distances to find IQR (filter out NaN values first)
    const validDistances = distances.filter((d) => !isNaN(d) && isFinite(d));
    if (validDistances.length === 0) {
        return boundsArray;
    }
    const sortedDistances = [...validDistances].sort((a, b) => a - b);
    const q1Index = Math.floor(sortedDistances.length * 0.25);
    const q3Index = Math.floor(sortedDistances.length * 0.75);
    const q1 = sortedDistances[q1Index] ?? 0;
    const q3 = sortedDistances[q3Index] ?? 0;
    const iqr = q3 - q1;

    // Use 1.5 * IQR as the outlier threshold (standard IQR method)
    const upperBound = q3 + 1.5 * iqr;

    // Filter out outliers
    const filtered = boundsArray.filter((_, i) => distances[i] <= upperBound);

    // Return filtered if we still have enough points, otherwise return original
    return filtered.length >= 1 ? filtered : boundsArray;
}

export const adjustMapViewport = (
    map: google.maps.Map,
    boundsArray: google.maps.LatLngBoundsLiteral[]
) => {
    // Filter outliers to focus on main cluster
    const filteredBounds = filterOutlierBounds(boundsArray);

    const viewport = new google.maps.LatLngBounds();
    filteredBounds.forEach((bounds) => {
        viewport.union(bounds);
    });
    // Use padding: 0 to prevent Google Maps from adding extra padding
    // which causes inconsistent zoom levels
    map.fitBounds(viewport, 0);
};

export const getIcon = (icon: IconOption) => {
    switch (icon) {
        case IconOption.PointerDark:
        case IconOption.PointerLight:
            return faLocationDot;
        case IconOption.House:
        default:
            return faHouse;
    }
};

export const getIconColor = (icon: IconOption, mapIconColor: string) => {
    switch (icon) {
        case IconOption.PointerDark:
            return { fill: 'rgba(0, 0, 0, 0.87)', backDrop: mapIconColor };
        case IconOption.PointerLight:
            return { fill: 'white', backDrop: mapIconColor };
        case IconOption.House:
        default:
            return { fill: mapIconColor, backDrop: 'white' };
    }
};

/** Simple Haversine formula to compute distance in meters. */
export function getDistanceMeters(
    loc1: google.maps.LatLngLiteral,
    loc2: google.maps.LatLngLiteral
) {
    const R = 6371e3; // Earth radius (m)
    const rad = Math.PI / 180;
    const dLat = (loc2.lat - loc1.lat) * rad;
    const dLng = (loc2.lng - loc1.lng) * rad;
    const a =
        Math.sin(dLat / 2) ** 2 +
        Math.cos(loc1.lat * rad) *
            Math.cos(loc2.lat * rad) *
            Math.sin(dLng / 2) ** 2;
    return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
}

export const BASE_ZOOM = 14;

/** Zoom-based distance threshold (in meters). Tweak to your needs. */
export function getDistanceThresholdForZoom(zoom: number) {
    const baseThreshold = 300; // e.g. 300m radius at zoom=14
    const referenceZoom = BASE_ZOOM;
    const factor = 2 ** (referenceZoom - zoom);
    return baseThreshold * factor;
}

/** Compute lat/lng centroid for a group of service areas. */
export function computeCentroid(
    group: NeighborhoodServiceAreaInfo[]
): google.maps.LatLngLiteral {
    let sumLat = 0;
    let sumLng = 0;
    let count = 0;
    for (const area of group) {
        const loc = area.placeInfo?.geometry?.location;
        if (loc) {
            sumLat += loc.lat;
            sumLng += loc.lng;
            count++;
        }
    }
    return count
        ? { lat: sumLat / count, lng: sumLng / count }
        : { lat: 0, lng: 0 };
}

export interface Cluster {
    centroid: google.maps.LatLngLiteral;
    serviceAreas: NeighborhoodServiceAreaInfo[];
    totalJobs: number;
}

export function clusterServiceAreas(
    serviceAreas: NeighborhoodServiceAreaInfo[],
    zoom: number
): Cluster[] {
    // Filter out service areas without valid location data upfront
    // This ensures computeCentroid always receives areas with valid locations
    const validServiceAreas = serviceAreas.filter(
        (area) => area.placeInfo?.geometry?.location
    );

    const threshold = getDistanceThresholdForZoom(zoom);
    const clusters: Cluster[] = [];

    for (const area of validServiceAreas) {
        const loc = area.placeInfo!.geometry!.location;

        let assigned = false;
        for (const cluster of clusters) {
            const dist = getDistanceMeters(cluster.centroid, loc);
            if (dist < threshold) {
                cluster.serviceAreas.push(area);
                cluster.totalJobs += area.jobCount;
                // Recalculate centroid
                cluster.centroid = computeCentroid(cluster.serviceAreas);
                assigned = true;
                break;
            }
        }

        if (!assigned) {
            clusters.push({
                centroid: loc,
                serviceAreas: [area],
                totalJobs: area.jobCount,
            });
        }
    }

    return clusters;
}
